import { RecommendationType } from 'common/constants/RecommendationType';
import {
  getCurrentOrganizationId,
  getMemberId,
  getMostRecentAssessmentPeriodId,
  getOrganizationMembers,
  getSelectedAssessmentPeriodId,
  isCurrentMemberOrgAdmin,
  isCurrentMemberTeamLeader,
  isCurrentUserStaffOrSuperAdmin,
} from 'common/selectors/auth';
import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Icon, Pagination } from 'semantic-ui-react';
import {
  HeaderCell,
  Table,
  TableCell,
  TableHeader,
  TableRow,
} from '../../../common/components/Table';
import { PageContent, PageHeader, PageSection } from '../../../common/styles/page';
import styled from 'styled-components';

import { teamApi } from 'areas/team-management/api/teamApi';

import DoughnutGauge from 'areas/dashboard/components/DoughnutGauge';
import LandingPage from 'common/layouts/AuthLayout/LandingPage';
import LoadingSpinner from 'common/components/LoadingSpinner';
import TopBarControls from 'common/layouts/AuthLayout/TopBarAssessmentPeriod';
import { TopBarContext } from 'common/layouts/AuthLayout/TopBarContext';

import {
  useApiPortalV1AssessmentsPlansOrganizationListQuery,
} from 'areas/assessment-schedule/api';

import { Employee } from 'common/types/Employee';

const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  min-width: 300px;
`;

export const TeamProjects = () => {
  const members = useSelector(getOrganizationMembers);
  const memberId = useSelector(getMemberId);
  const currentAssessmentPeriodId = useSelector(getSelectedAssessmentPeriodId);
  const isUserAdmin = useSelector(isCurrentUserStaffOrSuperAdmin);
  const isUserOrgAdmin = useSelector(isCurrentMemberOrgAdmin);
  const isUserTeamLeader = useSelector(isCurrentMemberTeamLeader);

  const { data: teams_data } = teamApi.useGetAllTeamsQuery();

  const team_plans_data = useApiPortalV1AssessmentsPlansOrganizationListQuery({
    assessmentPeriodId: currentAssessmentPeriodId,
    typeOf: RecommendationType.TEAM_RECOMMENDATIONS,
  }).data;

  const teamsList = teams_data?.results || [];
  const { data: org_goal_data } = useApiPortalV1AssessmentsPlansOrganizationListQuery({
    assessmentPeriodId: currentAssessmentPeriodId,
    typeOf: RecommendationType.ORGANIZATIONAL_GOALS,
  });

  const [areGoalsAssigned, setAreGoalsAssigned] = useState(false);
  const [teamProjectData, setTeamProjectData] = useState<any[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  const { setTopBarContent } = useContext(TopBarContext);
  const organizationId = useSelector(getCurrentOrganizationId);

/*   useEffect(() => {
    setTopBarContent(<TopBarControls />);
    return () => setTopBarContent(null);
  }, [setTopBarContent, organizationId]);
 */

  // Filter and Sorting
  const [nameFilter, setNameFilter] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
    key: 'name',
    direction: 'asc',
  });

  const handleSort = (key: string) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const PAGE_SIZE = 9;
  const [currentPage, setCurrentPage] = useState(1);

  const filteredTeamProjectData = useMemo(() => {
    const lowerName = nameFilter.trim().toLowerCase();
    const filtered = teamProjectData.filter((tp) => {
      const matchesName = !lowerName || (tp.name || '').toString().toLowerCase().includes(lowerName);
      return matchesName;
    });

    return [...filtered].sort((a: any, b: any) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [teamProjectData, nameFilter, sortConfig]);

  const paginatedTeamProjectData = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    return filteredTeamProjectData.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredTeamProjectData, currentPage]);

  const totalPages = Math.ceil(filteredTeamProjectData.length / PAGE_SIZE);

  useEffect(() => {
    // reset to first page whenever the underlying data changes
    setCurrentPage(1);
  }, [filteredTeamProjectData]);

  const navigate = useNavigate();

  useEffect(() => {
    if (org_goal_data && org_goal_data.length > 0) {
      setAreGoalsAssigned(true);
    } else {
      setAreGoalsAssigned(false);
    }

    if (teamsList && teamsList.length > 0) {
      let filteredTeams = [...teamsList];

      // Filter teams if Team Leader — show only teams the user leads
      if (isUserTeamLeader) {
        filteredTeams = teamsList.filter(team => (team.leader || team.leader_id) === memberId);
      }

      const processedData = filteredTeams.map((team: any) => {
        const matchingPlan = team_plans_data?.find(
          plan => plan.team === team.id
        );

        const leaderId = team.leader ?? team.leader_id ?? null;
        const leaderName = team.leader_name ?? lookupMemberName(leaderId);
        const percent_complete = Number(matchingPlan?.percent_complete) || 0;
        const total_tasks = matchingPlan?.total_task_count || 0;
        const completed_tasks = matchingPlan?.completed_task_count || 0;

        return {
          name: team.name,
          leader_name: leaderName,
          leader_id: leaderId,
          team_id: team.id,
          color: team.color || null,
          member_count: team.member_count ?? 0,
          action_plan_id: matchingPlan?.id || null,
          project_name: matchingPlan?.recommendation_name || null,
          progress: percent_complete,
          progress_summary: `${completed_tasks} of ${total_tasks} tasks completed`,
        };
      });

      const sortedData = [...processedData].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '')
      );
      // Team Leaders should only see teams with an assigned project; suppress 'Assign Project' rows
      const finalData = isUserTeamLeader ? sortedData.filter(tp => tp.action_plan_id != null) : sortedData;
      setTeamProjectData(finalData);
    }

    if (teamsList && team_plans_data && org_goal_data) {
      setIsLoaded(true);
    }
  }, [teamsList, team_plans_data, org_goal_data, members, memberId]);

  const lookupMemberName = (member_id: number | null | undefined) => {
    const member = members.find((member: Employee) => member.id === member_id);
    return member ? `${member.lastName || ''}${member.firstName ? `, ${member.firstName}` : ''}`.trim() : '* No Leader Assigned *';
  };

  const handleRowClick = (
    action_plan_id: number,
    team_id: number,
  ) => {
    if (action_plan_id) {
      navigate(`edit/${action_plan_id}`);
    } else {
      navigate(`new/${team_id}`);
    }
  };

  if (!isLoaded) {
    return <LoadingSpinner />;
  }

  // if the user is a team leader and does not have any assigned team projects, show a page that says you have no projects assigned
  const leaderHasAssignedProjects = teamProjectData && teamProjectData.some(tp => tp.action_plan_id);
  if (isUserTeamLeader && !leaderHasAssignedProjects) {
    return (
      <PageContent>
        <PageSection>
          <LandingPage
            title="No Team Projects Assigned"
            subtitle="You currently do not have any team projects assigned."
            buttonLabel=""
            navDestination="#"
          />
        </PageSection>
      </PageContent>
    );
  }





  if (!areGoalsAssigned) {
    // Team Leader Landing Page
    if (isUserTeamLeader) {
      return (
        <PageContent>
          <LandingPage
            title="Organizational Goals Needed"
            subtitle="Before selecting team projects, your nuLogic leader will need to select organizational goals. Please return after this is step is complete to access team projects."
            buttonLabel=""
            navDestination="#"
          />
        </PageContent>
      );
    } else
      return (
        <PageContent>
          <PageSection>
            <LandingPage
              title="Welcome to Team Projects!"
              subtitle="Before selecting team projects, you'll need to first select organizational goals..."
              buttonLabel="Let's Go!"
              navDestination="/app/org-goals"
            />
          </PageSection>
        </PageContent>
      );
  }

  return (
    <PageContent data-testid="admin-team-projects-page">
      <PageHeader title={'Team Projects'} data-testid="admin-team-projects-header"

        controls={<SearchInput
          type="text"
          placeholder="Search by team name..."
          value={nameFilter}
          onChange={(e) => {
            setNameFilter(e.target.value);
            setCurrentPage(1);
          }}
          aria-label="Search teams by name"
        />}
      />
      <PageSection padbottom data-testid="admin-team-projects-content">

        <Table selectable data-testid="admin-team-projects-table">
          <TableHeader>
            <HeaderCell selectable onClick={() => handleSort('name')} width={4}>
              Team Name{' '}
              {sortConfig.key === 'name' && (
                <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
              )}
            </HeaderCell>
            <HeaderCell selectable onClick={() => handleSort('leader_name')} width={3}>
              Leader{' '}
              {sortConfig.key === 'leader_name' && (
                <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
              )}
            </HeaderCell>
            <HeaderCell selectable onClick={() => handleSort('project_name')} width={6}>
              Project{' '}
              {sortConfig.key === 'project_name' && (
                <Icon name={sortConfig.direction === 'asc' ? 'caret up' : 'caret down'} />
              )}
            </HeaderCell>
            <HeaderCell width={3}>Progress</HeaderCell>
          </TableHeader>

          {paginatedTeamProjectData &&
            paginatedTeamProjectData.map((teamProject, index) => (
              <TableRow
                key={index}
                onClick={() =>
                  handleRowClick(
                    teamProject.action_plan_id,
                    teamProject.team_id,
                  )
                }
                $selectable
              >
                <TableCell $firstcol>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {teamProject.color && (
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '4px',
                          background: teamProject.color,
                          border: '1px solid #e0e0e0',
                          display: 'inline-block',
                        }}
                      />
                    )}
                    {teamProject.name}
                  </div>
                </TableCell>
                <TableCell>
                  {isUserAdmin || isUserOrgAdmin ? (
                    <span style={{ padding: '6px 10px', borderRadius: '12px' }}>
                      <span>{teamProject.leader_name}</span>
                    </span>
                  ) : (
                    <span>{teamProject.leader_name}</span>
                  )}
                </TableCell>

                {teamProject?.action_plan_id ? (
                  <>
                    <TableCell>{teamProject.project_name}</TableCell>
                    <TableCell style={{ gap: 10 }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                        <DoughnutGauge
                          color={teamProject.progress === 100 ? 'green' : ''}
                          complete={teamProject.progress}
                          percent={true}
                          size="tiny"
                        />
                        {teamProject.progress_summary}
                      </div>
                    </TableCell>
                  </>
                ) : (
                  <TableCell colSpan={2}>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        navigate(`new/${teamProject.team_id}`);
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`new/${teamProject.team_id}`);
                        }
                      }}
                      style={{
                        padding: '6px 10px',
                        borderRadius: '12px',
                        transition: 'background-color 0.2s',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor = '#d0d0d0';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Icon name="plus" color="green" size="small" />
                      <span>{'Assign Project'}</span>
                    </span>
                  </TableCell>
                )}
              </TableRow>
            ))}
        </Table>

        {totalPages > 1 && (
          <PageSection display="flex" justifyContent="space-between" alignItems="center">
            <div />
            <Pagination
              activePage={currentPage}
              totalPages={totalPages}
              onPageChange={(_, data) => setCurrentPage(data.activePage as number)}
              firstItem={null}
              lastItem={null}
              prevItem={{ content: '‹', icon: true }}
              nextItem={{ content: '›', icon: true }}
            />
          </PageSection>
        )}
      </PageSection>
    </PageContent>
  );
};
export default TeamProjects;
